<template>
  <div class="vp-chart-legends" :style="legendStyles">
    <div
      v-for="(item, index) in legends"
      :key="index"
    >
      <slot name="legend" :legend="item">
        <div 
          v-if="item.showLine" 
          :class="['vp-chart-legends__item', item.isActive ? 'active' : 'inactive']" :style="{ '--background-color': item.backgroundColor, '--active-font-color': item.activeFontColor, '--inactive-font-color': item.inactiveFontColor }"
          :tabindex="0"
          @click="item.handler"
          @keydown.enter="item.handler"
        >
          <vp-tooltip v-if="item.hoverText" v-bind="{ placement: 'bottom' }">
            <template #target>
               <p class="vp-chart-legends__item-label">{{ item.label }}</p>
            </template>
            <p class="vp-chart-legends__item-hover-text">{{ item.hoverText }}</p>
          </vp-tooltip>
          <p v-else class="vp-chart-legends__item-label">{{ item.label }}</p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VpChartLegends',
  props: {
    chartInstance: {
      type: Object,
      default: null,
    },
    chartData: {
      type: Object,
      default() {
        return {};
      },
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {};
  },
  computed: {
    chart() {
      return this.chartInstance ? this.chartInstance.getCurrentChart() : null;
    },
    legends() {
      if (!this.chart) return [];

      return (this.chartData.datasets || []).map((item, index) => {
        const { fill: isArea = false, label, yAxisID = 'yAxis1', showLine = true, legend = {}, pointBackgroundColor } = item;
        const { activeFontColor = '#FFFFFF', inactiveFontColor = '#4F4F4F', backgroundColor } = legend;

        const legendStyles = { 
          activeFontColor,
          inactiveFontColor,
          backgroundColor: backgroundColor || pointBackgroundColor
        };


        const isActive = this.chart.isDatasetVisible(index);
        const handler = (e) => {
          e.preventDefault();
          this.chart.setDatasetVisibility(index, !isActive);
          this.chart.update()
        };

        const _label = label && yAxisID === 'yAxis2' ? `${label} (right)` : label;
        
        return { ...item, data: null, label: _label, isArea, showLine, isActive, handler, ...legendStyles };
      });
    },
    legendStyles() {
      const { position = 'left', margin, itemPadding, itemMinWidth, gap } = this.styles;
      const positionConversion = {
        left: 'flex-start',
        right: 'flex-end',
        center: 'center',
      };

      const styles = {
        '--legends-position': positionConversion[position] || 'flex-start',
        '--legends-margin': margin || '5px 0 10px 24px',
        '--legends-gap': gap || '0.5em',
        /** Item styles */
        '--item-padding': itemPadding || '6px 10px',
        '--item-min-width': itemMinWidth || null,
      };

      return styles;
    },
  },
};
</script>

<style lang="scss">
.vp-chart-legends {
  display: flex;
  align-items: center;
  justify-content: var(--legends-position);
  gap: var(--legends-gap);
  padding: 0;
  flex-wrap: wrap;
  margin: var(--legends-margin);
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--item-padding);
    margin: 0;
    border-radius: 4px;
    min-width: var(--item-min-width);
    border: solid 1px var(--background-color);
    background-color: var(--background-color);
    cursor: pointer;
    &-label {
      margin: 0;
      padding: 2px 0;
      font-size: 14px;
      line-height: 1;
      font-weight: 500;
      color: var(--active-font-color);
      white-space: nowrap;
      user-select: none;
      -ms-user-select: none;
      -webkit-touch-callout: none;
    }

    &-hover-text {
      margin: 0;
      padding: 8px 12px;
    }

    // NOT ACTIVE LEGEND
    &.inactive {
      background-color: transparent !important;
      border: solid 1px #DBDBDB;
      .legend__item-point {
        &::before {
          background-color: var(--background-color) !important;
        }
      }
      p {
        color: var(--inactive-font-color) !important;
        font-weight: normal;
      }
    }
  }
}
</style>
